export type SharedScheduledPostStatus = 'Scheduled' | 'Draft' | 'Approved' | 'Posted';

export interface SharedScheduledPost {
  id: number;
  campaignId: number;
  campaignName: string;
  campaignType: string;
  title: string;
  description: string;
  dateKey: string;
  time: string;
  status: SharedScheduledPostStatus;
  language: string;
}

const LINKEDIN_SCHEDULE_STORAGE_KEY = 'hawk.linkedin.scheduledPosts.v1';
const LINKEDIN_SCHEDULE_RANGE_STORAGE_KEY = 'hawk.linkedin.scheduleRange.v1';
const LINKEDIN_SCHEDULE_LAYOUT_STORAGE_KEY = 'hawk.linkedin.scheduleLayout.v1';
const LINKEDIN_SCHEDULE_LAYOUT_DRAFT_STORAGE_KEY = 'hawk.linkedin.scheduleLayoutDraft.v1';
const LINKEDIN_SCHEDULE_DRAFT_POSTS_KEY = 'hawk.linkedin.scheduleDraftPosts.v1';

export interface SharedScheduleRange {
  startKey: string;
  endKey: string;
}

export function readSharedScheduledPosts(): SharedScheduledPost[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(LINKEDIN_SCHEDULE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function upsertSharedScheduledPost(post: SharedScheduledPost) {
  if (typeof window === 'undefined') return;

  const nextPosts = [
    ...readSharedScheduledPosts().filter((item) => item.id !== post.id),
    post,
  ];

  window.localStorage.setItem(LINKEDIN_SCHEDULE_STORAGE_KEY, JSON.stringify(nextPosts));
  window.dispatchEvent(new CustomEvent('linkedin-schedule-updated'));
}

export function readSharedScheduleRange(): SharedScheduleRange | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(LINKEDIN_SCHEDULE_RANGE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SharedScheduleRange>;
    if (typeof parsed.startKey !== 'string' || typeof parsed.endKey !== 'string') return null;
    return { startKey: parsed.startKey, endKey: parsed.endKey };
  } catch {
    return null;
  }
}

export function writeSharedScheduleRange(range: SharedScheduleRange) {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(LINKEDIN_SCHEDULE_RANGE_STORAGE_KEY, JSON.stringify(range));
  window.dispatchEvent(new CustomEvent('linkedin-schedule-range-updated', { detail: range }));
}

export function readSavedScheduleLayout<T>(): T | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(LINKEDIN_SCHEDULE_LAYOUT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeSavedScheduleLayout<T>(layout: T) {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(LINKEDIN_SCHEDULE_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  window.dispatchEvent(new CustomEvent('linkedin-schedule-layout-updated'));
}

export function readDraftScheduleLayout<T>(): T | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(LINKEDIN_SCHEDULE_LAYOUT_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeDraftScheduleLayout<T>(layout: T) {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(LINKEDIN_SCHEDULE_LAYOUT_DRAFT_STORAGE_KEY, JSON.stringify(layout));
  window.dispatchEvent(new CustomEvent('linkedin-schedule-layout-draft-updated'));
}

export interface ScheduleDraftPost {
  id: number;
  title: string;
  time: string;
  status: SharedScheduledPostStatus;
  language: string;
  campaignId: number;
  campaignName: string;
  campaignType: string;
  sourceDayKey: string;
  savedAt: string;
}

export function readScheduleDraftPosts(): ScheduleDraftPost[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(LINKEDIN_SCHEDULE_DRAFT_POSTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeScheduleDraftPosts(posts: ScheduleDraftPost[]) {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(LINKEDIN_SCHEDULE_DRAFT_POSTS_KEY, JSON.stringify(posts));
  window.dispatchEvent(new CustomEvent('linkedin-schedule-draft-posts-updated'));
}

export function removeSharedScheduledPost(postId: number) {
  if (typeof window === 'undefined') return;

  const nextPosts = readSharedScheduledPosts().filter((item) => item.id !== postId);
  window.localStorage.setItem(LINKEDIN_SCHEDULE_STORAGE_KEY, JSON.stringify(nextPosts));
  window.dispatchEvent(new CustomEvent('linkedin-schedule-updated'));
}
